import autoTable from "jspdf-autotable";
import { NUM, fmtDate, todayISO, unitLabel } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { colorBg, STATUS, METHOD_LABEL } from "@/components/ledger/ledgerUtils";
import { createDoc, partyLines, money, hexRgb, NAVY, INK, TEXT, SLATE, LINE, BAND, CREAM, WHITE, GREEN, GOLD } from "@/lib/docPdf";

// Varsayılan: yalnız ürün adı + fiyatlar açık (her açılışta bu ayarla başlar)
export const LEDGER_PDF_DEFAULTS = { code: false, name: true, desc: false, prices: true, detailed: false, notes: false };

const payDesc = (p) => {
  const bits = [];
  if (p.invoice_no) bits.push(`Fatura #${p.invoice_no}`);
  if (p.method === "cek" && p.due_date) bits.push(`Vade ${fmtDate(p.due_date)}`);
  if (p.from_whom) bits.push(p.from_whom);
  if (p.serial_no) bits.push(`Seri ${p.serial_no}`);
  if (p.document_no) bits.push(`Evrak ${p.document_no}`);
  if (p.note) bits.push(p.note);
  return bits.join(" · ") || "—";
};

/** Cari Defter PDF — sade, vektörel. opts: code / name / desc (ürün bilgileri), prices (kapalıysa TÜM tutarlar gizlenir),
 *  detailed (liste fiyatı + iskonto), notes (cari notları). */
export async function buildLedgerPdf({ contact: c = {}, rows = [], totals = {}, range = {}, notes = [], anyPending = false }, opts = {}) {
  const o = { ...LEDGER_PDF_DEFAULTS, ...opts };
  const showName = !!o.name, showDesc = !!o.desc, hasText = showName || showDesc;
  const showCode = !!o.code || !hasText;
  const prices = !!o.prices, detailed = prices && !!o.detailed;
  const kdvOn = !c.vat_exempt;
  const printNotes = !!o.notes && notes.length > 0;
  const pays = totals.payments || [];
  const pendingCount = rows.filter((r) => r.status === "bekliyor").length;
  const today = fmtDate(todayISO());
  const period = (range.start || range.end) ? `${range.start ? fmtDate(range.start) : "Başlangıç"} – ${range.end ? fmtDate(range.end) : today}` : "Tüm kayıtlar";
  const ctitle = String(c.title || "");
  const D = await createDoc({ title: "CARİ DEFTER", subtitle: prices ? "HESAP DÖKÜMÜ" : "TESLİM EDİLEN ÜRÜNLER", contLine: `${ctitle}  ·  ${period}  ·  devam` });
  const { pdf, F, T, fill, stroke, M, W, HEAD1 } = D;
  D.header(1);
  const rowH = D.partyCard(HEAD1 + 5, { name: ctitle, lines: partyLines(c), meta: [["BELGE TARİHİ", today], ["DÖNEM", period]] });

  // ---------- Ürün tablosu ----------
  const base = 7.6, pad = 1.4;
  const cols = [
    { key: "idx", label: "#", w: 10, align: "center", st: { textColor: SLATE, fontSize: base - 0.8, cellPadding: { top: pad, bottom: pad, left: 0.6, right: 0.6 } } },
    { key: "date", label: "TARİH", w: 17, align: "center", st: { textColor: TEXT, fontSize: base - 0.6 } },
  ];
  if (showCode) cols.push({ key: "code", label: "ÜRÜN KODU", w: hasText ? 22 : null, align: hasText ? "center" : "left", st: { font: F.mono, fontSize: base - 0.8, textColor: TEXT } });
  if (hasText) cols.push({ key: "name", label: showName && showDesc ? "ÜRÜN / AÇIKLAMA" : showName ? "ÜRÜN ADI" : "AÇIKLAMA", w: null, align: "left", st: { fontStyle: showName ? "bold" : "normal" } });
  cols.push({ key: "qty", label: "MİKTAR", w: 19, align: "center", st: {} });
  if (prices) {
    if (detailed) {
      cols.push({ key: "list", label: "LİSTE FİYATI", w: 22, align: "right", st: {} });
      cols.push({ key: "disc", label: "İSK.", w: 13, align: "center", st: { fontStyle: "bold", textColor: NAVY } });
    }
    cols.push({ key: "net", label: detailed ? "NET FİYAT" : "BİRİM FİYAT", w: 22, align: "right", st: {} });
    cols.push({ key: "total", label: "TUTAR", w: 25, align: "right", st: { fontStyle: "bold", textColor: NAVY } });
  }
  if (anyPending) cols.push({ key: "status", label: "DURUM", w: 17, align: "center", st: { fontStyle: "bold", fontSize: base - 1 } });
  const nameIdx = cols.findIndex((k) => k.key === "name");
  const fixedW = cols.reduce((s, k) => s + (k.w || 0), 0);
  const columnStyles = {};
  cols.forEach((k, i) => { columnStyles[i] = { cellWidth: k.w == null ? W - fixedW : k.w, halign: k.align, cellPadding: { top: pad, bottom: pad, left: 1.6, right: 1.6 }, ...k.st }; });
  const body = rows.map((r, i) => {
    const main = showName ? String(r.name || "") : String(r.description || "");
    const subs = [];
    if (showName && showDesc && r.description) subs.push(String(r.description));
    if (hasText && r.quoteNo) subs.push(`Teklif: ${r.quoteNo}`);
    const cell = {
      idx: String(r.sn ?? i + 1), date: fmtDate(r.date), code: String(r.code || "").trim() || "—",
      name: [main || "—", ...subs].join("\n"),
      qty: `${NUM(r.quantity)} ${unitLabel(r.unit)}`.trim(),
      list: money(r.list_price), disc: r.disc ? `%${NUM(r.disc)}` : "—", net: money(r.net_price), total: money(r.tutar),
      status: STATUS[r.status]?.label || "",
    };
    return cols.map((k) => cell[k.key]);
  });
  autoTable(pdf, D.tableBase({
    head: [cols.map((k) => ({ content: k.label, styles: { halign: k.align } }))],
    body: body.length ? body : [[{ content: "Bu dönemde kayıt bulunmuyor", colSpan: cols.length, styles: { halign: "center", textColor: SLATE } }]],
    startY: HEAD1 + 5 + rowH + 5,
    styles: { font: F.body, fontSize: base, cellPadding: { top: pad, bottom: pad, left: 2, right: 2 }, textColor: INK, lineColor: LINE, lineWidth: 0.2, overflow: "linebreak", valign: "middle", minCellHeight: base * 1.1 },
    columnStyles,
    didParseCell: (d) => {
      if (d.section !== "body" || !rows.length) return;
      const r = rows[d.row.index]; const key = cols[d.column.index].key;
      const bg = colorBg(r.color);
      if (bg) d.cell.styles.fillColor = hexRgb(bg);
      if (key === "status") d.cell.styles.textColor = STATUS[r.status]?.rgb || GREEN;
      if (key === "disc" && !r.disc) { d.cell.styles.textColor = [170, 178, 189]; d.cell.styles.fontStyle = "normal"; }
    },
    ...D.cellHooks(nameIdx, base, showName ? "bold" : "normal"),
  }));

  let y = pdf.lastAutoTable.finalY + 6;

  // ---------- Ödemeler (yalnız fiyatlar açıkken) ----------
  if (prices && pays.length) {
    y = D.ensure(y, 24) + 1;
    y = D.sectionTitle(y, "ÖDEMELER / TAHSİLATLAR", `${pays.length} ödeme  ·  toplam ${money(totals.paid)}`);
    autoTable(pdf, D.tableBase({
      head: [[{ content: "TARİH", styles: { halign: "center" } }, "YÖNTEM", "AÇIKLAMA", { content: "TUTAR", styles: { halign: "right" } }]],
      body: pays.map((p) => [fmtDate(p.date), METHOD_LABEL[p.method] || p.method || "—", payDesc(p), money(p.amount)]),
      startY: y,
      headStyles: { font: F.body, fontStyle: "bold", fillColor: BAND, textColor: NAVY, fontSize: 6.8, lineWidth: 0 },
      columnStyles: { 0: { cellWidth: 20, halign: "center" }, 1: { cellWidth: 28, fontStyle: "bold", textColor: NAVY }, 2: { cellWidth: W - 20 - 28 - 30, textColor: TEXT }, 3: { cellWidth: 30, halign: "right", fontStyle: "bold", textColor: GREEN } },
    }));
    y = pdf.lastAutoTable.finalY + 6;
  }

  // ---------- Toplamlar ----------
  if (prices) {
    const tRows = kdvOn
      ? [["ARA TOPLAM", money(totals.net)], ["KDV (%20)", money(totals.kdv)], ["GENEL TOPLAM", money(totals.total), true]]
      : [["TOPLAM", money(totals.total), true]];
    tRows.push(["TAHSİL EDİLEN", `− ${money(totals.paid)}`, false, GREEN]);
    y = D.ensure(y, D.totalsHeight(tRows.length) + 2);
    if (pendingCount) { T(F.body, "normal", 7, SLATE); pdf.text(`${pendingCount} kalem teslim bekliyor.`, M, y + 4.8); }
    y += D.totalsBox(y, tRows, Number(totals.open || 0)) + 6;
  } else {
    const byUnit = {};
    rows.forEach((r) => { const u = unitLabel(r.unit, "ADET"); byUnit[u] = (byUnit[u] || 0) + Number(r.quantity || 0); });
    const qtyStr = Object.entries(byUnit).map(([u, q]) => `${NUM(q)} ${u}`).join(" · ") || "—";
    const sigH = 24, sw = 58, gap = 4;
    y = D.ensure(y, sigH + 2);
    T(F.body, "bold", 7.8, NAVY); pdf.text(`Toplam ${rows.length} kalem`, M, y + 4.6);
    T(F.body, "normal", 7, SLATE); pdf.text(pdf.splitTextToSize(qtyStr, W - 2 * sw - gap - 8).slice(0, 3), M, y + 9, { lineHeightFactor: 1.35 });
    if (pendingCount) { T(F.body, "bold", 7, [180, 83, 9]); pdf.text(`${pendingCount} kalem teslim bekliyor`, M, y + 21); }
    [["TESLİM EDEN", COMPANY.name], ["TESLİM ALAN", ctitle]].forEach(([label, who], i) => {
      const sx = M + W - 2 * sw - gap + i * (sw + gap);
      stroke(LINE, 0.35); fill(WHITE); pdf.roundedRect(sx, y, sw, sigH, 2, 2, "FD");
      T(F.body, "bold", 6.4, NAVY); pdf.text(label, sx + 4, y + 5.6, { charSpace: 0.4 });
      T(F.body, "normal", 6, SLATE); pdf.text(pdf.splitTextToSize(who, sw - 8).slice(0, 2), sx + 4, y + 9.6);
      stroke(SLATE, 0.25); pdf.line(sx + 4, y + sigH - 6.5, sx + sw - 4, y + sigH - 6.5);
      T(F.body, "normal", 5.6, SLATE); pdf.text("Ad Soyad / İmza", sx + 4, y + sigH - 3);
    });
    y += sigH + 6;
  }

  // ---------- Cari notları (isteğe bağlı) ----------
  if (printNotes) {
    T(F.body, "normal", 7.4, INK);
    const blocks = notes.map((n) => pdf.splitTextToSize(String(n.text || ""), W - 16));
    const h = 11 + blocks.reduce((s, l) => s + l.length * 3.6 + 2.2, 0);
    y = D.ensure(y, h + 2);
    fill(CREAM); pdf.roundedRect(M, y, W, h, 2, 2, "F");
    fill(GOLD); pdf.rect(M, y + 2, 1.2, h - 4, "F");
    T(F.body, "bold", 7, NAVY); pdf.text("NOTLAR", M + 6, y + 6, { charSpace: 0.6 });
    let ly = y + 11.5;
    blocks.forEach((lines) => {
      fill(GOLD); pdf.circle(M + 7, ly - 1.1, 0.9, "F");
      T(F.body, "normal", 7.4, INK); pdf.text(lines, M + 10, ly, { lineHeightFactor: 1.38 });
      ly += lines.length * 3.6 + 2.2;
    });
    y += h + 6;
  }

  D.badgesAtBottom(y);
  return D.finish();
}
